import React, { useState, useEffect, useCallback } from 'react';
import { Sidebar } from '@/components/Sidebar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { 
  Send, 
  Plus, 
  Play, 
  Pause, 
  X, 
  Users, 
  Filter,
  List,
  Clock,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Loader2,
  Trash2,
  Eye,
  RefreshCw,
  MessageSquare,
  Smartphone,
  Package,
  FileSpreadsheet
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function BroadcastPage() {
  const [broadcasts, setBroadcasts] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [showDetail, setShowDetail] = useState(null);
  const [detailData, setDetailData] = useState(null);
  const [operators, setOperators] = useState([]);
  const [products, setProducts] = useState([]);
  const [availableProducts, setAvailableProducts] = useState([]);
  const [tags, setTags] = useState([]);
  const [instances, setInstances] = useState([]);
  const [importedLists, setImportedLists] = useState([]);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    message: '',
    target_type: 'all',
    channel: 'whatsapp',
    filters: {},
    phone_list: '',
    product_filter: '',
    selected_list_id: '',
    instance_name: '',
    delay_between_messages: 20,  // Mínimo 20 segundos para segurança
    max_recipients: ''
  });

  const fetchBroadcasts = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/broadcast`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setBroadcasts(data);
      }
    } catch (error) {
      console.error('Error fetching broadcasts:', error);
    }
  }, []);

  const fetchStats = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/broadcast/stats/summary`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  }, []);

  const fetchFormData = useCallback(async () => {
    const token = localStorage.getItem('token');
    try {
      const [opsRes, prodsRes, tagsRes, configRes, availProdsRes, listsRes] = await Promise.all([
        fetch(`${API_URL}/api/operators`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${API_URL}/api/products`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${API_URL}/api/tags`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${API_URL}/api/evolution/config`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${API_URL}/api/broadcast/products/available`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${API_URL}/api/import/lists`, { headers: { 'Authorization': `Bearer ${token}` } })
      ]);
      
      if (opsRes.ok) setOperators(await opsRes.json());
      if (prodsRes.ok) setProducts(await prodsRes.json());
      if (tagsRes.ok) setTags(await tagsRes.json());
      if (configRes.ok) {
        const config = await configRes.json();
        if (config.instances) setInstances(config.instances);
      }
      if (availProdsRes.ok) setAvailableProducts(await availProdsRes.json());
      if (listsRes.ok) setImportedLists(await listsRes.json());
    } catch (error) {
      console.error('Error fetching form data:', error);
    }
  }, []);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchBroadcasts(), fetchStats(), fetchFormData()]);
      setLoading(false);
    };
    loadData();
    
    // Auto refresh every 10 seconds for active broadcasts
    const interval = setInterval(() => {
      fetchBroadcasts();
      fetchStats();
    }, 10000);
    
    return () => clearInterval(interval);
  }, [fetchBroadcasts, fetchStats, fetchFormData]);

  const handleCreate = async () => {
    if (!formData.name || !formData.message) {
      toast.error('Preencha o nome e a mensagem da campanha');
      return;
    }
    
    setCreating(true);
    try {
      const token = localStorage.getItem('token');
      const payload = {
        ...formData,
        phone_list: formData.target_type === 'list' 
          ? formData.phone_list.split('\n').filter(p => p.trim()) 
          : null,
        max_recipients: formData.max_recipients ? parseInt(formData.max_recipients) : null
      };
      
      const res = await fetch(`${API_URL}/api/broadcast`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success(`Campanha criada com ${data.total_recipients} destinatários`);
        setShowCreate(false);
        setFormData({
          name: '',
          message: '',
          target_type: 'all',
          channel: 'whatsapp',
          filters: {},
          phone_list: '',
          product_filter: '',
          instance_name: '',
          delay_between_messages: 20,
          max_recipients: ''
        });
        fetchBroadcasts();
        fetchStats();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Erro ao criar campanha');
      }
    } catch (error) {
      toast.error('Erro ao criar campanha');
    }
    setCreating(false);
  };

  const handleAction = async (broadcastId, action) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/broadcast/${broadcastId}/${action}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        toast.success(
          action === 'start' ? 'Disparo iniciado!' :
          action === 'pause' ? 'Disparo pausado' :
          'Disparo cancelado'
        );
        fetchBroadcasts();
        fetchStats();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Erro na operação');
      }
    } catch (error) {
      toast.error('Erro na operação');
    }
  };

  const handleDelete = async (broadcastId) => {
    if (!window.confirm('Excluir esta campanha?')) return;
    
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/broadcast/${broadcastId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        toast.success('Campanha excluída');
        fetchBroadcasts();
        fetchStats();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Erro ao excluir');
      }
    } catch (error) {
      toast.error('Erro ao excluir');
    }
  };

  const handleViewDetail = async (broadcastId) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/broadcast/${broadcastId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setDetailData(data);
        setShowDetail(broadcastId);
      }
    } catch (error) {
      toast.error('Erro ao carregar detalhes');
    }
  };

  const getStatusBadge = (status) => {
    const config = {
      draft: { label: 'Rascunho', variant: 'secondary', icon: Clock },
      scheduled: { label: 'Agendado', variant: 'outline', icon: Clock },
      sending: { label: 'Enviando', variant: 'default', icon: Loader2 },
      completed: { label: 'Concluído', variant: 'success', icon: CheckCircle2 },
      paused: { label: 'Pausado', variant: 'warning', icon: Pause },
      cancelled: { label: 'Cancelado', variant: 'destructive', icon: XCircle }
    };
    const c = config[status] || config.draft;
    const Icon = c.icon;
    return (
      <Badge variant={c.variant} className="gap-1">
        <Icon className={`w-3 h-3 ${status === 'sending' ? 'animate-spin' : ''}`} />
        {c.label}
      </Badge>
    );
  };

  const getTargetIcon = (type) => {
    if (type === 'all') return <Users className="w-4 h-4" />;
    if (type === 'filter') return <Filter className="w-4 h-4" />;
    return <List className="w-4 h-4" />;
  };

  if (loading) {
    return (
      <div className="flex min-h-screen bg-slate-50">
        <Sidebar />
        <main className="flex-1 p-6 flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
        </main>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-slate-50">
      <Sidebar />
      <main className="flex-1 p-6 lg:p-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
          <div>
            <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Disparo em Massa
            </h1>
            <p className="text-slate-500 mt-1">Envie mensagens para múltiplos contatos</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => { fetchBroadcasts(); fetchStats(); }} data-testid="refresh-broadcasts">
              <RefreshCw className="w-4 h-4 mr-2" />
              Atualizar
            </Button>
            <Dialog open={showCreate} onOpenChange={setShowCreate}>
              <DialogTrigger asChild>
                <Button className="bg-emerald-600 hover:bg-emerald-700" data-testid="new-broadcast-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Nova Campanha
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Nova Campanha de Disparo</DialogTitle>
                </DialogHeader>
                
                <div className="space-y-4 py-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Nome da Campanha *</Label>
                      <Input 
                        placeholder="Ex: Promoção de Natal"
                        value={formData.name}
                        onChange={(e) => setFormData({...formData, name: e.target.value})}
                        data-testid="broadcast-name-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Canal de Envio</Label>
                      <Select 
                        value={formData.channel} 
                        onValueChange={(v) => setFormData({...formData, channel: v})}
                      >
                        <SelectTrigger data-testid="channel-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="whatsapp">
                            <div className="flex items-center gap-2">
                              <MessageSquare className="w-4 h-4 text-green-600" />
                              WhatsApp
                            </div>
                          </SelectItem>
                          <SelectItem value="sms">
                            <div className="flex items-center gap-2">
                              <Smartphone className="w-4 h-4 text-blue-600" />
                              SMS
                            </div>
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Mensagem *</Label>
                    <Textarea 
                      placeholder="Digite a mensagem. Use {nome} para personalizar com o primeiro nome do lead."
                      rows={4}
                      value={formData.message}
                      onChange={(e) => setFormData({...formData, message: e.target.value})}
                      data-testid="broadcast-message-input"
                    />
                    <p className="text-xs text-slate-500">
                      Variáveis: {'{nome}'} = primeiro nome, {'{nome_completo}'} = nome completo
                    </p>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Selecionar Destinatários</Label>
                    <Tabs value={formData.target_type} onValueChange={(v) => setFormData({...formData, target_type: v, product_filter: '', selected_list_id: ''})}>
                      <TabsList className="w-full grid grid-cols-5">
                        <TabsTrigger value="all" data-testid="target-all">
                          <Users className="w-4 h-4 mr-1" />
                          Todos
                        </TabsTrigger>
                        <TabsTrigger value="product" data-testid="target-product">
                          <Package className="w-4 h-4 mr-1" />
                          Produto
                        </TabsTrigger>
                        <TabsTrigger value="filter" data-testid="target-filter">
                          <Filter className="w-4 h-4 mr-1" />
                          Filtro
                        </TabsTrigger>
                        <TabsTrigger value="imported" data-testid="target-imported">
                          <FileSpreadsheet className="w-4 h-4 mr-1" />
                          Importada
                        </TabsTrigger>
                        <TabsTrigger value="list" data-testid="target-list">
                          <List className="w-4 h-4 mr-1" />
                          Manual
                        </TabsTrigger>
                      </TabsList>
                      
                      <TabsContent value="imported" className="mt-4">
                        <div className="space-y-3">
                          <Label className="text-xs">Selecione uma Lista Importada</Label>
                          {importedLists.length === 0 ? (
                            <p className="text-sm text-slate-500 text-center py-4">
                              Nenhuma lista importada. Vá em "Importar Base" para criar uma lista.
                            </p>
                          ) : (
                            <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto">
                              {importedLists.map(lst => (
                                <div 
                                  key={lst.id}
                                  onClick={() => setFormData({...formData, selected_list_id: lst.id, phone_list: lst.phones?.join('\n') || ''})}
                                  className={`p-3 border rounded-lg cursor-pointer transition-all ${
                                    formData.selected_list_id === lst.id 
                                      ? 'border-emerald-500 bg-emerald-50' 
                                      : 'border-slate-200 hover:border-slate-300'
                                  }`}
                                >
                                  <p className="font-medium text-sm">{lst.name}</p>
                                  <p className="text-xs text-slate-500">{lst.total?.toLocaleString()} números</p>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </TabsContent>
                      
                      <TabsContent value="product" className="mt-4">
                        <div className="space-y-3">
                          <Label className="text-xs">Selecione o Produto</Label>
                          <div className="grid grid-cols-2 gap-2">
                            {availableProducts.map(prod => (
                              <div 
                                key={prod.name}
                                onClick={() => setFormData({...formData, product_filter: prod.name})}
                                className={`p-3 border rounded-lg cursor-pointer transition-all ${
                                  formData.product_filter === prod.name 
                                    ? 'border-emerald-500 bg-emerald-50' 
                                    : 'border-slate-200 hover:border-slate-300'
                                }`}
                              >
                                <p className="font-medium text-sm">{prod.name}</p>
                                <p className="text-xs text-slate-500">{prod.lead_count} leads disponíveis</p>
                              </div>
                            ))}
                          </div>
                          {availableProducts.length === 0 && (
                            <p className="text-sm text-slate-500 text-center py-4">
                              Nenhum produto com leads cadastrados
                            </p>
                          )}
                        </div>
                      </TabsContent>
                      
                      <TabsContent value="filter" className="space-y-3 mt-4">
                        <div className="grid grid-cols-3 gap-3">
                          <div>
                            <Label className="text-xs">Produto</Label>
                            <Select 
                              value={formData.filters.product || 'all'} 
                              onValueChange={(v) => setFormData({...formData, filters: {...formData.filters, product: v === 'all' ? undefined : v}})}
                            >
                              <SelectTrigger><SelectValue placeholder="Todos" /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="all">Todos</SelectItem>
                                {availableProducts.map(prod => (
                                  <SelectItem key={prod.name} value={prod.name}>{prod.name} ({prod.lead_count})</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label className="text-xs">Status</Label>
                            <Select 
                              value={formData.filters.status || 'all'} 
                              onValueChange={(v) => setFormData({...formData, filters: {...formData.filters, status: v === 'all' ? undefined : v}})}
                            >
                              <SelectTrigger><SelectValue placeholder="Todos" /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="all">Todos</SelectItem>
                                <SelectItem value="sem_contato">Sem Contato</SelectItem>
                                <SelectItem value="agendamento">Agendamento</SelectItem>
                                <SelectItem value="venda">Venda</SelectItem>
                                <SelectItem value="audio_enviado">Audio Enviado</SelectItem>
                                <SelectItem value="nao_interesse">Nao Interesse</SelectItem>
                                <SelectItem value="trote">Trote</SelectItem>
                                <SelectItem value="reprovado">Reprovado</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label className="text-xs">Operador</Label>
                            <Select 
                              value={formData.filters.operator_id || 'all'} 
                              onValueChange={(v) => setFormData({...formData, filters: {...formData.filters, operator_id: v === 'all' ? undefined : v}})}
                            >
                              <SelectTrigger><SelectValue placeholder="Todos" /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="all">Todos</SelectItem>
                                {operators.map(op => (
                                  <SelectItem key={op.id} value={op.id}>{op.name}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                      </TabsContent>
                      
                      <TabsContent value="list" className="mt-4">
                        <Textarea 
                          placeholder="Digite um número por linha:&#10;5511999999999&#10;5521888888888"
                          rows={6}
                          value={formData.phone_list}
                          onChange={(e) => setFormData({...formData, phone_list: e.target.value})}
                          data-testid="phone-list-input"
                        />
                        <p className="text-xs text-slate-500 mt-1">
                          {formData.phone_list.split('\n').filter(p => p.trim()).length} números inseridos
                        </p>
                      </TabsContent>
                    </Tabs>
                  </div>
                  
                  <div className="grid grid-cols-3 gap-4">
                    {formData.channel === 'whatsapp' && (
                      <div className="space-y-2">
                        <Label>Instância WhatsApp</Label>
                        <Select 
                          value={formData.instance_name || 'default'} 
                          onValueChange={(v) => setFormData({...formData, instance_name: v === 'default' ? '' : v})}
                        >
                          <SelectTrigger><SelectValue placeholder="Padrão" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="default">Instância Padrão</SelectItem>
                            {instances.map(inst => (
                              <SelectItem key={inst.name} value={inst.name}>{inst.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                    <div className="space-y-2">
                      <Label>Intervalo entre msgs (seg)</Label>
                      <Input 
                        type="number"
                        min={20}
                        max={120}
                        value={formData.delay_between_messages}
                        onChange={(e) => setFormData({...formData, delay_between_messages: Math.max(20, parseInt(e.target.value) || 20)})}
                        data-testid="delay-input"
                      />
                      <p className="text-xs text-slate-500">Mínimo 20s (segurança)</p>
                    </div>
                    <div className="space-y-2">
                      <Label>Limite de envios</Label>
                      <Input 
                        type="number"
                        min={1}
                        placeholder="Sem limite"
                        value={formData.max_recipients}
                        onChange={(e) => setFormData({...formData, max_recipients: e.target.value})}
                        data-testid="max-recipients-input"
                      />
                      <p className="text-xs text-slate-500">Deixe vazio = todos</p>
                    </div>
                  </div>
                </div>
                
                <DialogFooter>
                  <Button variant="outline" onClick={() => setShowCreate(false)}>Cancelar</Button>
                  <Button 
                    onClick={handleCreate} 
                    disabled={creating}
                    className="bg-emerald-600 hover:bg-emerald-700"
                    data-testid="create-broadcast-btn"
                  >
                    {creating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
                    Criar Campanha
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
            <Card className="border-slate-200">
              <CardContent className="p-4">
                <p className="text-xs text-slate-500 uppercase tracking-wide">Campanhas</p>
                <p className="text-2xl font-bold text-slate-900">{stats.total_campaigns}</p>
              </CardContent>
            </Card>
            <Card className="border-slate-200">
              <CardContent className="p-4">
                <p className="text-xs text-slate-500 uppercase tracking-wide">Ativas</p>
                <p className="text-2xl font-bold text-emerald-600">{stats.active_campaigns}</p>
              </CardContent>
            </Card>
            <Card className="border-slate-200">
              <CardContent className="p-4">
                <p className="text-xs text-slate-500 uppercase tracking-wide">Concluídas</p>
                <p className="text-2xl font-bold text-slate-900">{stats.completed_campaigns}</p>
              </CardContent>
            </Card>
            <Card className="border-slate-200">
              <CardContent className="p-4">
                <p className="text-xs text-slate-500 uppercase tracking-wide">Msgs Enviadas</p>
                <p className="text-2xl font-bold text-emerald-600">{stats.total_messages_sent}</p>
              </CardContent>
            </Card>
            <Card className="border-slate-200">
              <CardContent className="p-4">
                <p className="text-xs text-slate-500 uppercase tracking-wide">Falhas</p>
                <p className="text-2xl font-bold text-red-600">{stats.total_messages_failed}</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Broadcasts List */}
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="text-lg">Campanhas</CardTitle>
            <CardDescription>Histórico de disparos em massa</CardDescription>
          </CardHeader>
          <CardContent>
            {broadcasts.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                <Send className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>Nenhuma campanha criada</p>
                <p className="text-sm">Clique em "Nova Campanha" para começar</p>
              </div>
            ) : (
              <div className="space-y-3">
                {broadcasts.map(broadcast => (
                  <div 
                    key={broadcast.id} 
                    className="flex items-center justify-between p-4 bg-white border border-slate-100 rounded-lg hover:border-slate-200 transition-colors"
                    data-testid={`broadcast-item-${broadcast.id}`}
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-full bg-emerald-50 flex items-center justify-center">
                        {getTargetIcon(broadcast.target_type)}
                      </div>
                      <div>
                        <h3 className="font-medium text-slate-900">{broadcast.name}</h3>
                        <div className="flex items-center gap-2 text-sm text-slate-500">
                          <span>{broadcast.total_recipients} destinatários</span>
                          <span>•</span>
                          <span>{new Date(broadcast.created_at).toLocaleDateString('pt-BR')}</span>
                          {broadcast.created_by_name && (
                            <>
                              <span>•</span>
                              <span>por {broadcast.created_by_name}</span>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-4">
                      {broadcast.status === 'sending' && (
                        <div className="w-32">
                          <Progress 
                            value={(broadcast.sent_count / broadcast.total_recipients) * 100} 
                            className="h-2"
                          />
                          <p className="text-xs text-slate-500 mt-1 text-center">
                            {broadcast.sent_count}/{broadcast.total_recipients}
                          </p>
                        </div>
                      )}
                      
                      {getStatusBadge(broadcast.status)}
                      
                      <div className="flex gap-1">
                        <Button 
                          variant="ghost" 
                          size="icon"
                          onClick={() => handleViewDetail(broadcast.id)}
                          data-testid={`view-broadcast-${broadcast.id}`}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        
                        {broadcast.status === 'draft' && (
                          <>
                            <Button 
                              variant="ghost" 
                              size="icon"
                              className="text-emerald-600 hover:text-emerald-700"
                              onClick={() => handleAction(broadcast.id, 'start')}
                              data-testid={`start-broadcast-${broadcast.id}`}
                            >
                              <Play className="w-4 h-4" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="icon"
                              className="text-red-600 hover:text-red-700"
                              onClick={() => handleDelete(broadcast.id)}
                              data-testid={`delete-broadcast-${broadcast.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </>
                        )}
                        
                        {broadcast.status === 'sending' && (
                          <Button 
                            variant="ghost" 
                            size="icon"
                            className="text-amber-600 hover:text-amber-700"
                            onClick={() => handleAction(broadcast.id, 'pause')}
                            data-testid={`pause-broadcast-${broadcast.id}`}
                          >
                            <Pause className="w-4 h-4" />
                          </Button>
                        )}
                        
                        {broadcast.status === 'paused' && (
                          <>
                            <Button 
                              variant="ghost" 
                              size="icon"
                              className="text-emerald-600 hover:text-emerald-700"
                              onClick={() => handleAction(broadcast.id, 'start')}
                              data-testid={`resume-broadcast-${broadcast.id}`}
                            >
                              <Play className="w-4 h-4" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="icon"
                              className="text-red-600 hover:text-red-700"
                              onClick={() => handleAction(broadcast.id, 'cancel')}
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Detail Dialog */}
        <Dialog open={!!showDetail} onOpenChange={() => { setShowDetail(null); setDetailData(null); }}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Detalhes da Campanha</DialogTitle>
            </DialogHeader>
            
            {detailData && (
              <div className="space-y-6 py-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-slate-500 uppercase">Nome</p>
                    <p className="font-medium">{detailData.name}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 uppercase">Status</p>
                    {getStatusBadge(detailData.status)}
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 uppercase">Destinatários</p>
                    <p className="font-medium">{detailData.total_recipients}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 uppercase">Enviados / Falhas</p>
                    <p className="font-medium">
                      <span className="text-emerald-600">{detailData.sent_count}</span>
                      {' / '}
                      <span className="text-red-600">{detailData.failed_count}</span>
                    </p>
                  </div>
                </div>
                
                <div>
                  <p className="text-xs text-slate-500 uppercase mb-2">Mensagem</p>
                  <div className="bg-slate-50 p-3 rounded-lg text-sm whitespace-pre-wrap">
                    {detailData.message}
                  </div>
                </div>
                
                {detailData.deliveries && detailData.deliveries.length > 0 && (
                  <div>
                    <p className="text-xs text-slate-500 uppercase mb-2">Histórico de Envios</p>
                    <div className="max-h-64 overflow-y-auto space-y-2">
                      {detailData.deliveries.slice(0, 50).map(d => (
                        <div 
                          key={d.id} 
                          className="flex items-center justify-between text-sm p-2 bg-slate-50 rounded"
                        >
                          <span className="font-mono">{d.phone}</span>
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-slate-500">
                              {new Date(d.sent_at).toLocaleTimeString('pt-BR')}
                            </span>
                            {d.status === 'sent' ? (
                              <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                            ) : (
                              <XCircle className="w-4 h-4 text-red-600" />
                            )}
                          </div>
                        </div>
                      ))}
                      {detailData.deliveries.length > 50 && (
                        <p className="text-xs text-slate-500 text-center">
                          ... e mais {detailData.deliveries.length - 50} envios
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
